import { Formik } from "formik";
import { useMemo } from "react";
import { connect } from "react-redux";
import { useParams } from "react-router";
import { toast } from "react-toastify";
import { Container, Grid, MenuItem } from "@material-ui/core";

import { useAppDispatch, useAppSelector } from "../../../hooks";
import { history } from "../../..";
import { ContainmentPadFormSchema } from "../schema/containment-pad-form-schema";
import { STORAGE_AREA_STATUSES } from "../storage-area-status";
import PageHero from "../../shared/page-hero";
import storageAreaService from "../../../services/storageAreaService";
import {
  getContainmentPad,
  setContainmentPad,
} from "../../../store/storageAreaStore";
import { SetContainmentPadAction } from "../../../interface/redux-types/SetContainmentPadAction";
import { ContainmentPad } from "../../../interface/api/ContainmentPad";
import { ContainmentPadFormValues } from "../../../interface/formik/ContainmentPadFormValues";
import FormErrorMessage from "../../shared/form-error-message";
import FormTextField from "../../shared/form-text-field";
import FormSelect from "../../shared/form-select";
import Button from "../../shared/Button";

const ContainmentPadView = ({}) => {
  const { id }: any = useParams();
  const dispatch = useAppDispatch();
  const containmentPad = useAppSelector(getContainmentPad);

  useMemo(() => {
    if (id > 0) {
      storageAreaService.getContainmentPad(id).then((response) => {
        if (response.ok) {
          const containmentPad = response.data as ContainmentPad;
          const setContainmentPadAction: SetContainmentPadAction = {
            containmentPad,
          };
          dispatch(setContainmentPad(setContainmentPadAction));
        } else {
          toast.error(response.originalError.message);
        }
      });
    }
  }, []);

  const onSubmit = (
    values: ContainmentPadFormValues,
    setSubmitting: Function
  ) => {
    storageAreaService
      .saveContainmentPad(values as ContainmentPad)
      .then((response) => {
        if (response.ok) {
          history.push(`/storage-areas`);
        } else {
          const data = response.data as string;
          toast.error(data);
          setSubmitting(false);
        }
      });

    setSubmitting(false);
  };

  const initialValues: ContainmentPadFormValues = containmentPad
    ? containmentPad
    : {
        id: 0,
        name: "",
        status: "Active",
        capacity: 67,
        sequenceNumber: undefined,
      };

  // A closed pad is finalized (name is "Containment Pad X Month D, YYYY") — lock
  // its number and capacity from edits.
  const isClosed = containmentPad?.status === "Closed";

  // Physical containment pads are numbered 1-4; the picked number drives the name.
  const CONTAINMENT_PAD_NUMBERS = Array.from({ length: 4 }, (_, i) => i + 1);

  return (
    <Container maxWidth="xl">
      <PageHero
        title={
          id > 0
            ? `${containmentPad ? containmentPad.name : id}`
            : "New Containment Pad"
        }
        subtitle={id > 0 ? `Edit Containment Pad.` : "Add Containment Pad."}
        showBackButton={true}
      />
      <Formik
        initialValues={initialValues}
        validationSchema={ContainmentPadFormSchema}
        onSubmit={(values, { setSubmitting }) =>
          onSubmit(values, setSubmitting)
        }
        enableReinitialize={true}
      >
        {({ values, handleSubmit, isSubmitting }) => (
          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                {isClosed ? (
                  <FormTextField
                    name="name"
                    label="Name"
                    value={values.name}
                    disabled={true}
                  />
                ) : (
                  <>
                    <FormSelect
                      name="sequenceNumber"
                      label="Containment Pad Number"
                      value={values.sequenceNumber ?? null}
                      items={CONTAINMENT_PAD_NUMBERS.map((n) => (
                        <MenuItem key={n} value={n}>
                          {n}
                        </MenuItem>
                      ))}
                    />
                    <FormErrorMessage name="sequenceNumber" />
                  </>
                )}
              </Grid>
              <Grid item xs={12} md={4}>
                <FormSelect
                  name="status"
                  label="Status"
                  value={values.status}
                  items={STORAGE_AREA_STATUSES.map((status) => (
                    <MenuItem key={status} value={status}>
                      {status}
                    </MenuItem>
                  ))}
                />
                <FormErrorMessage name="status" />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormTextField
                  name="capacity"
                  type="number"
                  label="Capacity"
                  value={values.capacity}
                  disabled={isClosed}
                />
                <FormErrorMessage name="capacity" />
              </Grid>
              <Grid item xs={12} md={4}>
                <Button
                  label="Save"
                  color="primary"
                  variant="contained"
                  type="submit"
                  disabled={isSubmitting}
                />
              </Grid>
            </Grid>
          </form>
        )}
      </Formik>
    </Container>
  );
};

export default connect()(ContainmentPadView);
